/**
 * Cliente HTTP fino. Todas as rotas passam por aqui.
 *
 * Em dev usamos o proxy do Vite (`/api` → http://localhost:3333).
 * Em prod, VITE_API_URL substitui o prefixo.
 */
import { tidyText } from "./format";
const API_BASE = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

const TOKEN_KEY = "bi:token";

export const tokenStore = {
  get(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  set(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
  },
};

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = tokenStore.get();
  const hasBody = body !== undefined;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? safeJSON(text) : null;
  if (!res.ok) {
    if (typeof window !== "undefined" && path !== "/auth/login" && token === tokenStore.get()) {
      if (res.status === 401) window.dispatchEvent(new Event("siag:session-ended"));
      else if (res.status === 403 && path !== "/me") window.dispatchEvent(new Event("siag:refresh-session"));
    }
    const err = (data as { error?: string; message?: string; details?: unknown }) ?? {};
    throw new ApiError(res.status, err.error ?? "ERR", err.message ?? res.statusText, err.details);
  }
  return data as T;
}

/**
 * Os textos que a API monta (insights, alertas, memórias de cálculo, rótulos de
 * comparação) ainda trazem travessão no meio da frase. Eles são limpos aqui, na
 * chegada, para nenhuma tela mostrar "—" no meio de um texto (ver `tidyText`).
 */
function safeJSON(text: string): unknown {
  try { return JSON.parse(text, (_key, value) => (typeof value === "string" ? tidyText(value) : value)); } catch { return null; }
}

/**
 * Upload de arquivo (multipart).
 *
 * Não passa por `request()` porque aquele serializa JSON e fixa o
 * `Content-Type`. Num multipart o boundary é gerado pelo browser, e definir o
 * cabeçalho à mão quebra o parse do lado do servidor.
 */
async function upload<T>(path: string, file: File): Promise<T> {
  const token = tokenStore.get();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  const text = await res.text();
  const data = text ? safeJSON(text) : null;
  if (!res.ok) {
    const err = (data as { error?: string; message?: string; details?: unknown }) ?? {};
    // 422 = arquivo entendido mas com conteúdo que não fecha; o corpo traz os
    // problemas e a tela precisa deles, então vai junto em `details`.
    throw new ApiError(res.status, err.error ?? "ERR", err.message ?? uploadFallbackMessage(res), data);
  }
  return data as T;
}

/**
 * Mensagem quando a resposta NÃO veio da API — proxy (nginx/Traefik) respondeu
 * antes, com HTML ou corpo vazio. Por HTTP/2 o `statusText` chega VAZIO, e a
 * tela só mostra a caixa de erro quando há texto: um 413 do nginx aparecia
 * como "nada aconteceu". Foi exatamente o caso do extrato de 5 MB.
 */
function uploadFallbackMessage(res: Response): string {
  if (res.status === 413) {
    return "O arquivo é maior do que o servidor aceita (HTTP 413). O limite fica no proxy, não na API (ver apps/web/nginx.conf).";
  }
  return res.statusText || `Falha no envio (HTTP ${res.status}).`;
}

/**
 * Upload com campos extras no multipart (escopo, alvo, legenda).
 *
 * Os campos vão ANTES do arquivo de propósito: o `@fastify/multipart` só entrega
 * em `file.fields` o que passou no fluxo antes do binário. Invertida a ordem, o
 * servidor recebe o arquivo sem saber onde pendurá-lo.
 */
async function uploadWithFields<T>(
  path: string,
  file: Blob,
  fields: Record<string, string | null | undefined>,
  fileName?: string,
): Promise<T> {
  const token = tokenStore.get();
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (v != null && v !== "") form.append(k, v);
  }
  // Blob da fila offline não tem nome; o File da câmera tem.
  form.append("file", file, fileName ?? (file instanceof File ? file.name : "foto.jpg"));
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  const text = await res.text();
  const data = text ? safeJSON(text) : null;
  if (!res.ok) {
    const err = (data as { error?: string; message?: string }) ?? {};
    throw new ApiError(res.status, err.error ?? "ERR", err.message ?? uploadFallbackMessage(res), data);
  }
  return data as T;
}

/**
 * Baixa um binário protegido por JWT (foto de evidência) e devolve uma object
 * URL. Não dá para usar `<img src="/api/...">` direto: o token vive no
 * localStorage e não é enviado numa requisição de imagem.
 *
 * Quem chama é responsável por `URL.revokeObjectURL` ao desmontar.
 */
async function blobUrl(path: string): Promise<string> {
  const token = tokenStore.get();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new ApiError(res.status, "ERR", `Falha ao carregar o arquivo (HTTP ${res.status}).`);
  return URL.createObjectURL(await res.blob());
}

export const api = {
  get:  <T,>(p: string) => request<T>("GET", p),
  upload,
  uploadWithFields,
  blobUrl,
  post: <T,>(p: string, body?: unknown) => request<T>("POST", p, body),
  put:  <T,>(p: string, body?: unknown) => request<T>("PUT", p, body),
  patch: <T,>(p: string, body?: unknown) => request<T>("PATCH", p, body),
  del:  <T,>(p: string) => request<T>("DELETE", p),
};
